import { useEffect, useState } from "react";

import { GridOn, UploadFile } from "@mui/icons-material";
import i18next from "i18next";
import { Card, CardBody } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";
import Button from "../components/Button";
import * as samplesServices from "../services/samplesServices";

interface Props {
	aspectRatio?: string;
	imagePreview?: string;
	className?: string;
	onRemoveFile?: () => void;
	onCancel?: () => void;
	onUpload?: () => void;
	setErrors?: (errors: any) => void;
	setLoading?: (loading: boolean) => void;
}

const FileUploader = ({
	setLoading: setLoadingParam,
	aspectRatio,
	onCancel,
	className = "",
	onUpload,
	setErrors = () => {},
}: Props) => {
	const [loading, setLoading] = useState(false);
	const [fileDropped, setFileDropped] = useState<string | ArrayBuffer | null>(null);

	useEffect(() => {
		setLoadingParam?.(loading);
	}, [loading]);

	const { getRootProps, getInputProps } = useDropzone({
		multiple: false,
		accept: {
			"text/csv": [".csv"],
		},
		onDrop: (acceptedFiles) => {
			const reader = new FileReader();
			reader.readAsDataURL(acceptedFiles[0]);
			reader.onloadend = (e) => {
				setFileDropped(reader.result);
			};
		},
	});

	const uploadFile = async () => {
		if (!fileDropped) return;

		setErrors?.(null);

		try {
			setLoading(true);

			const formData = new FormData();
			const base64Data = fileDropped as string;
			// Extraer solo los datos base64 sin el prefijo
			const base64Content = base64Data.split(",")[1];
			// Convertir el contenido base64 en un archivo "virtual" con codificación UTF-8
			const byteCharacters = atob(base64Content);
			const byteNumbers = new Array(byteCharacters.length);
			for (let i = 0; i < byteCharacters.length; i++) {
				byteNumbers[i] = byteCharacters.charCodeAt(i);
			}
			const byteArray = new Uint8Array(byteNumbers);
			const blob = new Blob([byteArray], { type: "text/csv;charset=utf-8" });
			formData.append("file", blob, "massive_create.csv");
			await samplesServices.uploadFile(formData);
			onUpload?.();
			toast.success(i18next.t("upload-success"));
		} catch (error: any) {
			const errors = error?.response?.data?.detail;
			if (errors) {
				setErrors?.(
					Array.isArray(errors)
						? errors
								?.map((element) =>
									i18next.t(element.error, {
										value: element.value,
										row: element.row,
									})
								)
								.join("\n") ?? null
						: errors
				);
				toast.error(i18next.t("upload-error"));
			} else {
				toast.error(i18next.t("error"));
			}
		} finally {
			setLoading(false);
		}
	};

	return (
		<>
			<Card style={{ width: "100%", aspectRatio }} className={"bg-white " + className}>
				<CardBody className="py-5">
					{fileDropped && (
						<div {...getRootProps({ className: "dropzone h-100" })}>
							<div className="d-flex align-items-center justify-content-center flex-column text-center h-100">
								<GridOn fontSize="large" color="primary" />
								<h5 className="mt-2">{i18next.t(loading ? "uploading-file" : "uploaded-file")}</h5>
								{!loading && (
									<Button
										buttonClass="border-0"
										label={i18next.t("discard")}
										onClick={() => {
											setErrors(null);
											setFileDropped(null);
										}}
										secondary
									/>
								)}
							</div>
						</div>
					)}
					{!fileDropped && (
						<div {...getRootProps({ className: "dropzone h-100" })}>
							<input {...getInputProps()} />
							<div className="d-flex align-items-center justify-content-center flex-column text-center h-100">
								<UploadFile fontSize="large" color="primary" />
								<h5 className="mt-3">{i18next.t("drop-file")}</h5>
								<p className="text-secondary">
									{i18next.t("drop-here-or")}{" "}
									<a href="/" onClick={(e) => e.preventDefault()}>
										{i18next.t("search-it")} {i18next.t("in-your-computer")}
									</a>
								</p>
							</div>
						</div>
					)}
				</CardBody>
			</Card>
			{(onUpload || onCancel) && (
				<div className="d-flex mt-4 justify-content-end">
					{onCancel && (
						<Button
							label={i18next.t("cancel")}
							secondary
							disabled={loading}
							onClick={onCancel}
							className="ms-4"
						/>
					)}
					{onUpload && (
						<Button
							label={i18next.t("upload")}
							disabled={!fileDropped}
							onClick={uploadFile}
							className="ms-4"
							loading={loading}
						/>
					)}
				</div>
			)}
		</>
	);
};

export default FileUploader;
